import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Container, Typography, Button, Grid, Stack, Chip,
  Card, CardContent, Divider, Alert,
} from '@mui/material';
import { Link as LinkIcon, Verified, ArrowForward } from '@mui/icons-material';
import { C, Section } from './theme';

const plans = [
  {
    id: 'starter', name: 'Starter Tier', badge: null, image: '/assets/tier-1.png',
    desc: 'A focused link package for new or smaller sites finding their footing',
    price: '₹19,999', save: 'Save 20%',
    term: 'One-time package · Delivered in 10–14 days',
    renew: 'Great as a monthly recurring add-on',
    specsText: '10 Backlinks · DA/DR 30–50 · Manual Outreach',
    features: [
      '10 manually placed backlinks',
      'DA/DR 30–50 authority range',
      'Niche-relevant, contextual placements',
      'Mixed dofollow / nofollow (natural profile)',
      'Detailed placement report with live URLs',
      'Google-safe, 100% white-hat outreach',
    ],
    specs: [
      { icon: <LinkIcon fontSize="small" />, text: '10 Backlinks Delivered' },
      { icon: <Verified fontSize="small" />, text: 'DA/DR 30–50 Authority' },
    ],
  },
  {
    id: 'growth', name: 'Growth Tier', badge: 'RECOMMENDED', image: '/assets/tier-2.png',
    desc: 'The most popular package for sites actively pushing to rank higher',
    price: '₹46,999', save: 'Save 30%',
    term: 'One-time package · Delivered in 14–18 days',
    renew: 'Best value as a monthly recurring plan',
    specsText: '25 Backlinks · DA/DR 40–70 · Guest Posts Included',
    features: [
      '25 manually placed backlinks',
      'DA/DR 40–70 authority range',
      'Includes 5 guest posts on real blogs',
      'Custom anchor text strategy',
      'Detailed placement report with live URLs',
      'Google-safe, 100% white-hat outreach',
    ],
    specs: [
      { icon: <LinkIcon fontSize="small" />, text: '25 Backlinks Delivered' },
      { icon: <Verified fontSize="small" />, text: 'DA/DR 40–70 Authority' },
    ],
  },
  {
    id: 'authority', name: 'Authority Tier', badge: null, image: '/assets/tier-3.png',
    desc: 'Aggressive, high-authority link building for competitive keywords',
    price: '₹89,999', save: 'Save 35%',
    term: 'One-time package · Delivered in 18–25 days',
    renew: 'Ideal for agencies managing multiple clients',
    specsText: '60 Backlinks · DA/DR 60–90+ · Priority Support',
    features: [
      '60 manually placed backlinks',
      'DA/DR 60–90+ authority range',
      'Includes 12 premium guest posts',
      'Dedicated link-building strategist',
      'Priority delivery & 24/7 support',
      'Google-safe, 100% white-hat outreach',
    ],
    specs: [
      { icon: <LinkIcon fontSize="small" />, text: '60 Backlinks Delivered' },
      { icon: <Verified fontSize="small" />, text: 'DA/DR 60–90+ Authority' },
    ],
  },
];

const VpsPricing = () => {
  const navigate = useNavigate();
  const [hovered, setHovered] = useState(null);

  const handleSelectPlan = (plan) => {
    const serializablePlan = {
      id: plan.id,
      name: plan.name,
      badge: plan.badge,
      desc: plan.desc,
      price: plan.price,
      save: plan.save,
      term: plan.term,
      renew: plan.renew,
      specsText: plan.specsText,
      features: plan.features,
      category: 'Backlink Package',
    };
    navigate('/checkout', { state: { plan: serializablePlan } });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <Section id="pricing" sx={{ background: '#FFFFFF', borderBottom: `1px solid ${C.border}` }}>
      <Container maxWidth="lg">

        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Chip label="MANUAL · WHITE-HAT · GOOGLE-SAFE" size="small" sx={{
            background: C.primaryLight, color: C.primary, fontWeight: 700,
            border: `1px solid ${C.primary}30`, fontSize: '0.76rem', mb: 2.5,
          }} />
          <Typography variant="h2" sx={{
            fontWeight: 800, fontSize: { xs: '1.85rem', md: '2.5rem' },
            color: C.textPrimary, letterSpacing: '-0.03em', mb: 1.5,
          }}>
            Choose Your Backlink Package
          </Typography>
          <Typography sx={{ color: C.textSecondary, maxWidth: 620, mx: 'auto', fontSize: '1.02rem', lineHeight: 1.6 }}>
            Every link is manually researched and placed by our outreach team —
            no automation, no PBNs, just real sites that move rankings.
          </Typography>
        </Box>

        <Grid container spacing={3} alignItems="stretch">
          {plans.map((plan) => {
            const isRec = plan.badge === 'RECOMMENDED';
            const isHov = hovered === plan.id;
            return (
              <Grid size={{ xs: 12, sm: 6, lg: 4 }} key={plan.id}>
                <Card
                  onMouseEnter={() => setHovered(plan.id)}
                  onMouseLeave={() => setHovered(null)}
                  elevation={0}
                  sx={{
                    height: '100%',
                    display: 'flex', flexDirection: 'column',
                    borderRadius: '18px',
                    border: isRec ? `2px solid ${C.primary}` : `1px solid ${C.border}`,
                    boxShadow: isRec || isHov
                      ? '0 16px 36px rgba(109,40,217,0.14)'
                      : '0 2px 10px rgba(0,0,0,0.03)',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    transform: isHov ? 'translateY(-6px)' : 'none',
                    position: 'relative', overflow: 'visible',
                    background: isRec ? '#FBFAFF' : '#FFFFFF',
                  }}
                >
                  {isRec && (
                    <Box sx={{
                      position: 'absolute', top: -14, left: '50%', transform: 'translateX(-50%)',
                      background: C.primary, color: '#FFFFFF', fontWeight: 800,
                      fontSize: '0.7rem', letterSpacing: '0.1em',
                      px: 2.5, py: 0.6, borderRadius: '20px', whiteSpace: 'nowrap',
                    }}>
                      MOST POPULAR
                    </Box>
                  )}

                  <Box sx={{ px: 3, pt: 3.5 }}>
                    <Box component="img" src={plan.image} alt={plan.name}
                      sx={{ width: '100%', height: 150, objectFit: 'contain', borderRadius: '12px' }} />
                  </Box>

                  <CardContent sx={{ p: 3.5, pt: 2, flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', color: C.textPrimary, mb: 0.5 }}>
                      {plan.name}
                    </Typography>
                    <Typography sx={{ color: C.textMuted, fontSize: '0.82rem', lineHeight: 1.5, mb: 2.5, minHeight: 40 }}>
                      {plan.desc}
                    </Typography>

                    <Box sx={{ mb: 1 }}>
                      <Stack direction="row" alignItems="baseline" spacing={0.5}>
                        <Typography sx={{ fontWeight: 900, fontSize: '2.3rem', color: C.textPrimary, lineHeight: 1 }}>
                          {plan.price}
                        </Typography>
                      </Stack>
                      <Chip label={plan.save} size="small" sx={{
                        mt: 1, background: C.successLight, color: C.success,
                        fontWeight: 700, fontSize: '0.72rem', border: `1px solid ${C.success}40`,
                      }} />
                    </Box>
                    <Typography sx={{ color: C.textMuted, fontSize: '0.74rem', mb: 0.3 }}>{plan.term}</Typography>
                    <Typography sx={{ color: C.textMuted, fontSize: '0.74rem', mb: 2.5 }}>{plan.renew}</Typography>

                    <Divider sx={{ borderColor: C.border, mb: 2.5 }} />

                    <Stack spacing={1.3} sx={{ mb: 3.5, flex: 1 }}>
                      {plan.features.map((f) => (
                        <Stack key={f} direction="row" spacing={1.5} alignItems="flex-start">
                          <Box sx={{ color: C.primary, display: 'flex', mt: 0.3 }}><LinkIcon fontSize="small" /></Box>
                          <Typography sx={{ color: C.textSecondary, fontSize: '0.86rem', fontWeight: 500 }}>
                            {f}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>

                    <Button
                      fullWidth
                      variant="contained"
                      endIcon={<ArrowForward />}
                      onClick={() => handleSelectPlan(plan)}
                      sx={{
                        background: isRec ? C.primary : C.textPrimary,
                        color: '#FFFFFF', fontWeight: 700, fontSize: '0.9rem',
                        py: 1.4, borderRadius: '10px', textTransform: 'none',
                        boxShadow: 'none',
                        '&:hover': {
                          background: isRec ? C.primaryHover : '#0F0620',
                          boxShadow: 'none',
                        },
                      }}
                    >
                      Get Started
                    </Button>
                    <Typography sx={{ color: C.textMuted, fontSize: '0.72rem', textAlign: 'center', mt: 1 }}>
                      Secure Checkout via Razorpay
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        <Alert severity="info" icon={false} sx={{
          mt: 5, background: C.primaryLight, border: `1px solid ${C.primary}25`,
          borderRadius: '12px', color: C.textPrimary, fontSize: '0.85rem',
          maxWidth: 800, mx: 'auto', textAlign: 'center',
        }}>
          💡 <strong>Need something custom?</strong> Every package can be tailored — pick your target URL,
          keywords, and preferred niches at checkout, or contact us for a bespoke campaign.
        </Alert>

      </Container>
    </Section>
  );
};

export default VpsPricing;
